import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    quote: "Alita Captures exceeded all our expectations. They captured our wedding day with such artistry and emotion. Every photo tells a story, and we couldn't be happier with the results.",
    author: "Sophie & Marc",
    role: "Wedding Clients",
  },
  {
    quote: "Working with Alita was an absolute pleasure. Their attention to detail and creative vision transformed our brand imagery. Highly recommend for any professional photography needs.",
    author: "Jennifer Dubois",
    role: "Creative Director, Luxe Brand",
  },
  {
    quote: "The portrait session was magical. They made me feel so comfortable and the final images were stunning. I've never felt more confident in photos.",
    author: "Amélie Tremblay",
    role: "Portrait Client",
  },
];

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      className="py-24 md:py-32 bg-charcoal"
      data-testid="testimonials-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Header */}
          <p className="font-sans text-xs tracking-superwide text-terracotta mb-4">
            TESTIMONIALS
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-cream mb-16">
            Client Love Letters
          </h2>

          {/* Testimonial */}
          <div className="relative">
            <Quote size={48} className="text-terracotta/30 mx-auto mb-8" />
            
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <blockquote className="font-serif text-2xl sm:text-3xl text-cream/90 leading-relaxed mb-8 italic">
                "{testimonials[currentIndex].quote}"
              </blockquote>
              <div>
                <p className="font-sans text-sm tracking-widest text-cream">
                  {testimonials[currentIndex].author}
                </p>
                <p className="font-body text-sm text-stone mt-1">
                  {testimonials[currentIndex].role}
                </p>
              </div>
            </motion.div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-12">
              <button
                onClick={prevTestimonial}
                className="p-3 border border-sand/30 text-cream hover:border-terracotta hover:text-terracotta transition-colors"
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentIndex ? 'bg-terracotta' : 'bg-sand/30'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={nextTestimonial}
                className="p-3 border border-sand/30 text-cream hover:border-terracotta hover:text-terracotta transition-colors"
                data-testid="testimonial-next"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
